import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

/**
 * mode = "create" | "edit"
 * user (edit mode only): { id, email, name, agency, role }
 * onSaved(user) — parent refreshes list
 */
export default function UserFormDialog({ open, onOpenChange, mode = "create", user = null, onSaved }) {
  const isEdit = mode === "edit";
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [agency, setAgency] = useState("");
  const [role, setRole] = useState("agent");
  const [agentType, setAgentType] = useState("employee");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateKey, setTemplateKey] = useState("");

  useEffect(() => {
    if (!open) return;
    // Load role templates for auto-permission-apply. Silently no-op for
    // non-admins (endpoint 403s) — the dropdown just stays empty.
    api.get("/admin/permissions/catalog")
      .then(({ data }) => setTemplates(data.role_templates || []))
      .catch(() => setTemplates([]));
    if (isEdit && user) {
      setEmail(user.email || "");
      setName(user.name || "");
      setAgency(user.agency || "");
      setRole(user.role || "agent");
      setAgentType(user.agent_type || "employee");
      setPassword("");
      setTemplateKey("");
    } else {
      setEmail("");
      setName("");
      setAgency("");
      setRole("agent");
      setAgentType("employee");
      setPassword("");
      setTemplateKey("");
    }
  }, [open, isEdit, user]);

  // Auto-fill role when a template is picked so the two stay in sync.
  useEffect(() => {
    if (!templateKey) return;
    const t = templates.find((x) => x.key === templateKey);
    if (t?.role) setRole(t.role);
  }, [templateKey, templates]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !name.trim()) {
      toast.error("Name and email are required");
      return;
    }
    if (!isEdit && password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (isEdit && password && password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const body = {
          email: email.trim(),
          name: name.trim(),
          agency: agency.trim(),
          role,
          agent_type: role === "agent" ? agentType : "",
        };
        if (password) body.password = password;
        const { data } = await api.put(`/admin/users/${user.id}`, body);
        toast.success("User updated");
        onSaved?.(data);
      } else {
        const { data } = await api.post("/admin/users", {
          email: email.trim(),
          name: name.trim(),
          agency: agency.trim() || null,
          role,
          agent_type: role === "agent" ? agentType : null,
          password,
          permission_template: templateKey || null,
        });
        if (data?.welcome_emailed) toast.success(`User created — welcome email sent to ${data.email}`);
        else toast.warning(`User created, but the welcome email failed${data?.welcome_email_error ? `: ${data.welcome_email_error}` : ""}`);
        onSaved?.(data);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="user-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#0B132B]">
            {isEdit ? "Edit user" : "Add user"}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#6C757D]">
            {isEdit
              ? "Update account details. Leave password blank to keep the current one."
              : "Create an account directly. The user can sign in immediately."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="uf-name" className="text-xs uppercase tracking-[0.16em] font-bold text-[#0B132B]">Full name</Label>
            <Input
              id="uf-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe"
              required
              data-testid="user-form-name"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="uf-email" className="text-xs uppercase tracking-[0.16em] font-bold text-[#0B132B]">Email</Label>
            <Input
              id="uf-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@agency.com"
              required
              data-testid="user-form-email"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="uf-agency" className="text-xs uppercase tracking-[0.16em] font-bold text-[#0B132B]">Agency</Label>
            <Input
              id="uf-agency"
              value={agency}
              onChange={(e) => setAgency(e.target.value)}
              placeholder="Optional"
              data-testid="user-form-agency"
            />
          </div>

          <div className="grid gap-2">
            <Label className="text-xs uppercase tracking-[0.16em] font-bold text-[#0B132B]">Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="agent" data-testid="user-form-role-agent">Agent</SelectItem>
                <SelectItem value="agency_admin" data-testid="user-form-role-agency-admin">Agency Admin</SelectItem>
                <SelectItem value="support" data-testid="user-form-role-support">Support</SelectItem>
                <SelectItem value="admin" data-testid="user-form-role-admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {role === "agent" && (
            <div className="grid gap-2">
              <Label className="text-xs uppercase tracking-[0.16em] font-bold text-[#0B132B]">
                Agent type
              </Label>
              <Select value={agentType} onValueChange={setAgentType}>
                <SelectTrigger data-testid="user-form-agent-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee" data-testid="user-form-agent-type-employee">
                    Employee — works for the agency (no commission visibility)
                  </SelectItem>
                  <SelectItem value="subagent" data-testid="user-form-agent-type-subagent">
                    Subagent — independent contractor under this agency
                  </SelectItem>
                </SelectContent>
              </Select>
              <div className="text-[11px] text-[#6C757D] italic">
                Subagents can set their own commission markup and require agency-admin approval before production.
              </div>
            </div>
          )}

          {!isEdit && templates.length > 0 && (
            <div className="grid gap-2">
              <Label className="text-xs uppercase tracking-[0.16em] font-bold text-[#0B132B]">
                Auto-apply permission template <span className="text-[10px] font-normal text-[#6C757D] normal-case tracking-normal">(optional)</span>
              </Label>
              <Select value={templateKey || "__none__"} onValueChange={(v) => setTemplateKey(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="user-form-template"><SelectValue placeholder="No template — permissions blank" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No template — permissions blank</SelectItem>
                  {templates.map((t) => (
                    <SelectItem key={t.key} value={t.key} data-testid={`user-form-template-${t.key}`}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {templateKey && (
                <div className="text-[11px] text-[#6C757D] italic">
                  {templates.find((t) => t.key === templateKey)?.description}
                </div>
              )}
            </div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="uf-password" className="text-xs uppercase tracking-[0.16em] font-bold text-[#0B132B]">
              {isEdit ? "New password (optional)" : "Password"}
            </Label>
            <Input
              id="uf-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={isEdit ? "Leave blank to keep current" : "Min. 6 characters"}
              required={!isEdit}
              data-testid="user-form-password"
            />
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
              data-testid="user-form-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#0B132B] hover:bg-[#0B132B]/90"
              data-testid="user-form-submit"
            >
              {saving && <Loader2 size={14} className="mr-2 animate-spin" />}
              {isEdit ? "Save changes" : "Create user"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
